import React from 'react';
import './ThemeAnalysis.css';

function ThemeAnalysis({ themes }) {
  if (!themes) return null;

  const { topThemes, emergingThemes, wordCloud } = themes;

  const getSentimentColor = (sentiment) => {
    if (sentiment > 0.5) return '#10B981'; // Green - positive
    if (sentiment > 0) return '#3B82F6'; // Blue - somewhat positive
    if (sentiment > -0.3) return '#6B7280'; // Gray - neutral
    if (sentiment > -0.6) return '#F59E0B'; // Orange - somewhat negative
    return '#EF4444'; // Red - negative
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return '↗️';
    if (trend === 'declining') return '↘️';
    if (trend === 'stable') return '→';
    if (trend === 'new') return '✨';
    return '';
  };

  return (
    <div className="theme-analysis">
      <h3 className="theme-analysis__title">Theme Analysis</h3>

      {/* Top Themes */}
      {topThemes && topThemes.length > 0 && (
        <section className="theme-analysis__section">
          <h4 className="theme-analysis__subtitle">Key Themes</h4>
          <div className="theme-analysis__themes-grid">
            {topThemes.map((theme, index) => {
              // Safety check for theme object
              if (!theme || !theme.theme) return null;
              
              return (
                <div key={index} className="theme-analysis__theme-card">
                  <div className="theme-analysis__theme-header">
                    <h5 className="theme-analysis__theme-name">{theme.theme}</h5>
                    <span className="theme-analysis__theme-trend">
                      {getTrendIcon(theme.trend)}
                    </span>
                  </div>

                  <div className="theme-analysis__theme-mentions">
                    <span className="theme-analysis__mentions-count">{theme.mentions || 0}</span>
                    <span className="theme-analysis__mentions-label">mentions</span>
                  </div>

                  {theme.sentiment !== undefined && (
                    <div 
                      className="theme-analysis__sentiment-bar"
                      style={{
                        backgroundColor: getSentimentColor(theme.sentiment || 0),
                        width: `${Math.abs(theme.sentiment || 0) * 100}%`
                      }}
                    >
                      <span className="theme-analysis__sentiment-label">
                        {(theme.sentiment || 0) > 0 ? 'Positive' : 'Negative'} ({((theme.sentiment || 0) * 100).toFixed(0)}%)
                      </span>
                    </div>
                  )}

                  <div className="theme-analysis__keywords">
                    {theme.keywords && theme.keywords.slice(0, 5).map((keyword, i) => (
                      <span key={i} className="theme-analysis__keyword">
                        {keyword}
                      </span>
                    ))}
                  </div>

                  {theme.peakPeriod && (
                    <div className="theme-analysis__peak-period">
                      Peak: {theme.peakPeriod}
                    </div>
                  )}

                  {theme.insight && (
                    <div className="theme-analysis__theme-insight">
                      💡 {theme.insight}
                    </div>
                  )}

                  {/* Associated Moods */}
                  {theme.associatedMoods && Object.keys(theme.associatedMoods).length > 0 && (
                    <div className="theme-analysis__associated-moods">
                      <div className="theme-analysis__moods-label">Associated moods:</div>
                      <div className="theme-analysis__moods-list">
                        {Object.entries(theme.associatedMoods)
                          .sort(([, a], [, b]) => b - a)
                          .map(([mood, count]) => (
                            <div key={mood} className="theme-analysis__mood-item">
                              <span className="theme-analysis__mood-name">{mood}</span>
                              <span className="theme-analysis__mood-count">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Emerging Themes */}
      {emergingThemes && emergingThemes.length > 0 && (
        <section className="theme-analysis__section">
          <h4 className="theme-analysis__subtitle">
            <span className="theme-analysis__subtitle-icon">✨</span>
            Emerging Themes
          </h4>
          <div className="theme-analysis__emerging-grid">
            {emergingThemes.map((theme, index) => {
              if (!theme || !theme.theme) return null;
              
              return (
                <div key={index} className="theme-analysis__emerging-card">
                  <div className="theme-analysis__emerging-header">
                    <h5 className="theme-analysis__emerging-name">{theme.theme}</h5>
                    <span className="theme-analysis__new-badge">New</span>
                  </div>
                  <div className="theme-analysis__emerging-mentions">
                    {theme.mentions || 0} mentions
                    {theme.firstMentioned && (
                      <> since{' '}
                        {new Date(theme.firstMentioned).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </>
                    )}
                  </div>
                  {theme.note && (
                    <div className="theme-analysis__emerging-note">{theme.note}</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Word Cloud */}
      {wordCloud && wordCloud.length > 0 && (
        <section className="theme-analysis__section">
          <h4 className="theme-analysis__subtitle">Frequently Used Words</h4>
          <div className="theme-analysis__word-cloud">
            {wordCloud.slice(0, 30).map((word, index) => {
              // Safety checks for word object
              if (!word || !word.word || typeof word.count !== 'number') return null;
              
              const maxCount = wordCloud[0]?.count || 1;
              const size = 0.7 + ((word.count || 0) / maxCount) * 1.3; // 0.7 to 2.0
              // Use a gradient based on frequency instead of sentiment
              const hue = 250 - (((word.count || 0) / maxCount) * 100); // Blue to purple gradient
              const color = `hsl(${hue}, 70%, 60%)`;
              
              return (
                <span
                  key={index}
                  className="theme-analysis__word"
                  style={{
                    fontSize: `${size}em`,
                    color: color,
                    opacity: 0.6 + ((word.count || 0) / maxCount) * 0.4
                  }}
                  title={`${word.count} times`}
                >
                  {word.word}
                </span>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

export default ThemeAnalysis;
